/**
 * reportWebVitals — POSTs Core Web Vitals metrics to the backend.
 *
 * Transport strategy:
 *   1. `navigator.sendBeacon`   — preferred; survives page unload, no
 *                                 extra network round-trip on FCP/LCP.
 *   2. `fetch` (keepalive:true) — fallback when sendBeacon is absent
 *                                 (very rare: some old Safari versions).
 *   3. Silent drop              — if neither is available (e.g. Worker
 *                                 context, unit tests). Never throws.
 *
 * The payload shape is intentionally flat so it can be stored in a
 * simple append-only table without JSON extraction.
 *
 * Backend endpoint: POST /api/v1/analytics/web-vitals
 *
 * TODO (separate PR): persist to `web_vitals_observations` table.
 * Proposed schema:
 *   CREATE TABLE web_vitals_observations (
 *     id              uuid PRIMARY KEY DEFAULT gen_random_uuid(),
 *     organization_id uuid REFERENCES organizations(id),
 *     tenant_id       uuid,
 *     session_id      text,      -- navigator.connection / performance.now fingerprint
 *     name            text,      -- "CLS" | "INP" | "LCP" | "FCP" | "TTFB"
 *     value           float8,    -- raw metric value (ms or score)
 *     rating          text,      -- "good" | "needs-improvement" | "poor"
 *     navigation_type text,      -- "navigate" | "reload" | "back-forward" | "prerender"
 *     page            text,      -- window.location.pathname at collection time
 *     collected_at    timestamptz DEFAULT now()
 *   );
 *   CREATE INDEX ON web_vitals_observations (organization_id, name, collected_at DESC);
 *
 * Spec: docs/CHAT-UI-IMPROVEMENT-DESIGN.md §TrackA — Core Web Vitals
 */

import type { Metric } from 'web-vitals';

/** Wire format sent to the backend. */
export interface WebVitalsPayload {
  name: string;
  value: number;
  rating: string;
  id: string;
  navigationType: string;
  page: string;
}

const ENDPOINT = '/api/v1/analytics/web-vitals';

/**
 * Report a single metric to the backend analytics endpoint.
 *
 * Best-effort: failures are logged to the console in development but
 * never rethrown. A metrics reporting failure must never affect the
 * user experience.
 */
export function reportWebVitals(metric: Metric): void {
  if (typeof window === 'undefined') return;

  const payload: WebVitalsPayload = {
    name:           metric.name,
    value:          metric.value,
    rating:         metric.rating,
    id:             metric.id,
    navigationType: metric.navigationType ?? 'navigate',
    page:           window.location.pathname,
  };

  const body = JSON.stringify(payload);
  const headers = { 'Content-Type': 'application/json' };

  // Attempt sendBeacon first (ideal for metrics that fire on page hide).
  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    const blob = new Blob([body], { type: 'application/json' });
    const queued = navigator.sendBeacon(ENDPOINT, blob);
    if (queued) return;
    // sendBeacon returned false (payload too large or queue full) — fall
    // through to fetch.
  }

  // Fallback: keepalive fetch (survives navigation on modern browsers).
  fetch(ENDPOINT, {
    method:    'POST',
    body,
    headers,
    keepalive: true,
  }).catch((err: unknown) => {
    if (import.meta.env.DEV) {
      console.debug('[web-vitals] Failed to report metric:', metric.name, err);
    }
  });
}
